/**
 * Hiring-demand board — the frozen baseline.
 *
 * WHY THERE IS A BUTTON FOR THIS AT ALL. The trimester round compares what a
 * unit planned against what happened, and the plan lives in Airtable — which
 * exposes **no field-level revision history**. A start date that moves leaves no
 * trace, so there is no "read the HDB as it was on 1 September" call to make.
 * The only way a plan survives is if somebody copies it, deliberately, at the
 * start. That is what this does.
 *
 * TWO KINDS, and the distinction matters. A BASELINE is what the trimester is
 * measured against; a SNAPSHOT is a checkpoint along the way. Freezing a new
 * baseline mid-trimester is allowed — plans get re-cut — and the older one stays
 * as history rather than being overwritten, which is why the bar names the date
 * of the one in force rather than just saying "baselined".
 */
import { useRef, useState } from 'react';
import { Archive, Camera, Loader2, Upload, X } from 'lucide-react';
import type { DemandSnapshot } from '../../../types/taDemand';
import { relativeTime } from './demandUi';

export function BaselineBar({
  snapshots,
  busy,
  onFreeze,
  onImport,
}: {
  snapshots: DemandSnapshot[];
  busy: boolean;
  onFreeze: (kind: 'baseline' | 'snapshot') => void;
  /**
   * Freeze an Airtable CSV export — the only way to baseline a past date.
   * Resolves when the server has stored it, so the picked file survives a
   * failure instead of making the lead find it again.
   */
  onImport: (file: { csv: string; label: string; takenAt: string; id: string }) => Promise<boolean>;
}) {
  const baseline = snapshots.find((s) => s.kind === 'baseline') ?? null;
  const latest = snapshots[0] ?? null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[13px]">
      <Archive className="h-3.5 w-3.5 shrink-0 text-gray-400" />
      {baseline ? (
        <span className="text-gray-700">
          <b className="font-semibold">Baseline:</b> {baseline.label}
          <span className="ml-1.5 text-gray-400">
            {baseline.rowCount} roles · {relativeTime(baseline.takenAt)}
            {baseline.takenBy ? ` · ${baseline.takenBy}` : ''}
          </span>
        </span>
      ) : (
        /*
         * Not a warning tone on purpose. A period with no baseline is the normal
         * state of a round that has not started, and a red banner on every load
         * would train people to ignore the bar that carries the button.
         */
        <span className="text-gray-500">
          <b className="font-semibold text-gray-700">No frozen baseline yet.</b> Airtable keeps no
          history of its own, so nothing can be compared against the plan until one is taken.
        </span>
      )}

      {latest && latest.kind !== 'baseline' && (
        <span className="text-gray-400">
          Last snapshot {relativeTime(latest.takenAt)} · {latest.rowCount} roles
        </span>
      )}

      <span className="ml-auto flex shrink-0 items-center gap-1.5">
        <ImportExport busy={busy} onImport={onImport} />
        <button
          type="button"
          onClick={() => onFreeze('snapshot')}
          disabled={busy}
          title="Store a copy of the HDB as it is now, without changing what the trimester is measured against"
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
          Take a snapshot
        </button>
        <button
          type="button"
          onClick={() => onFreeze('baseline')}
          disabled={busy}
          title={
            baseline
              ? 'Re-baseline: the current one is kept as history'
              : 'Freeze the HDB as what this trimester is measured against'
          }
          className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {busy && <Loader2 className="h-3 w-3 animate-spin" />}
          {baseline ? 'Re-freeze baseline' : 'Freeze baseline'}
        </button>
      </span>
    </div>
  );
}

/**
 * Importing an export.
 *
 * The live freeze cannot reach into the past — Airtable exposes no field-level
 * revision history — so a round that started before this tool did can only be
 * baselined from a file somebody exported at the time. T3-2026 is exactly that
 * case.
 *
 * The DATE IS ASKED FOR, and defaults to the file's own last-modified date
 * rather than today. A CSV downloaded a week after the table it describes would
 * otherwise stamp the baseline with the download, and every comparison made
 * against it would be measured from the wrong day. It is a two-field form for
 * exactly that reason: the file alone does not say what it is a picture of.
 */
function ImportExport({
  busy,
  onImport,
}: {
  busy: boolean;
  onImport: (file: { csv: string; label: string; takenAt: string; id: string }) => Promise<boolean>;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<{ name: string; csv: string } | null>(null);
  const [date, setDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function choose(file: File | undefined) {
    if (!file) return;
    setError(null);
    try {
      setPicked({ name: file.name, csv: await file.text() });
      setDate(localDate(new Date(file.lastModified)));
    } catch {
      setError('That file could not be read.');
    }
  }

  function reset() {
    setPicked(null);
    setDate('');
    setError(null);
    if (input.current) input.current.value = '';
  }

  return (
    <>
      <input
        ref={input}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        aria-label="Airtable CSV export of the Hiring Database"
        onChange={(e) => void choose(e.target.files?.[0])}
      />
      {!picked ? (
        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={busy}
          title="Freeze a CSV export of the HDB — the only way to baseline a date that has already passed"
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          <Upload className="h-3 w-3" />
          Import an export
        </button>
      ) : (
        <span className="flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-100 px-2 py-1">
          <span className="max-w-[180px] truncate text-xs text-gray-700" title={picked.name}>
            {picked.name}
          </span>
          <label className="flex items-center gap-1 text-[11px] text-gray-500">
            as of
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              aria-label="The date this export describes"
              className="rounded-sm border border-gray-300 px-1 py-0.5 text-[11px] focus:border-blue-500 focus:outline-none"
            />
          </label>
          <button
            type="button"
            disabled={busy || !date}
            onClick={() => {
              // Reset only once the server has it. Clearing on click threw the
              // file away on a failure, and the lead had to go and find it
              // again to retry something that had not happened.
              void onImport({
                csv: picked.csv,
                label: `HDB as exported on ${date}`,
                takenAt: `${date}T00:00:00.000Z`,
                id: date,
              }).then((ok) => {
                if (ok) reset();
              });
            }}
            className="rounded-sm bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Freeze as baseline'}
          </button>
          <button
            type="button"
            onClick={reset}
            aria-label="Cancel the import"
            className="grid h-4 w-4 place-items-center rounded-sm text-gray-400 hover:bg-gray-100"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      )}
      {error && (
        <span role="alert" className="text-xs text-rose-700">
          {error}
        </span>
      )}
    </>
  );
}

/**
 * `YYYY-MM-DD` in the reader's OWN calendar.
 *
 * `toISOString().slice(0, 10)` is UTC, so a file last modified at 00:30 in
 * Berlin comes back as the previous day — and that date becomes the baseline's
 * id and the point every later comparison is measured from.
 */
function localDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
